import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class AuthErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Store error details in state
    this.setState({
      error,
      errorInfo
    });

    // Use original console if available for debugging
    const originalConsole = (window as any).__originalConsole;
    if (originalConsole && originalConsole.error) {
      originalConsole.error('AuthErrorBoundary caught error:', error, errorInfo);
    } else {
      console.error('Auth error caught by boundary:', error, errorInfo);
    }

    // Log to database what error we're catching
    const errorLogger = (window as any).errorLogger;
    if (errorLogger) {
      errorLogger.createComponentLogger('AuthErrorBoundary').error('Error caught by boundary', {
        errorMessage: error.message,
        errorStack: error.stack,
        errorName: error.name,
        componentStack: errorInfo.componentStack
      });
    }

    // If we catch an auth error, clear everything and redirect
    if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
      if (errorLogger) {
        errorLogger.createComponentLogger('AuthErrorBoundary').error('WOULD CLEAR STORAGE BUT DISABLED FOR DEBUGGING', {
          errorMessage: error.message
        });
      }
      // TEMPORARILY DISABLED FOR DEBUGGING
      // localStorage.clear();
      // sessionStorage.clear();
      // window.location.href = '/login';
    }
  }

  public render() {
    if (this.state.hasError) {
      // TEMPORARILY DISABLED FOR DEBUGGING
      // localStorage.clear();
      // sessionStorage.clear();
      // window.location.href = '/login';
      // return null;

      // Show error instead of clearing - using window.__originalConsole for debugging
      const originalConsole = (window as any).__originalConsole;
      if (originalConsole && originalConsole.error) {
        originalConsole.error('AuthErrorBoundary caught error:', this.state);
      }

      // Show error with details for debugging
      const { error, errorInfo } = this.state;
      return <div style={{ padding: '20px', color: 'red', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '12px', backgroundColor: '#ffe6e6', border: '2px solid red', borderRadius: '8px', maxWidth: '800px', margin: '20px auto' }}>
        <h3 style={{ color: 'darkred', marginTop: 0 }}>⚠️ AuthErrorBoundary caught an error</h3>

        {error && (
          <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: 'white', borderRadius: '4px' }}>
            <strong>Error:</strong> {error.toString()}
            {error.stack && (
              <div style={{ marginTop: '10px', fontSize: '10px', color: '#666' }}>
                <strong>Stack:</strong><br />
                {error.stack}
              </div>
            )}
          </div>
        )}

        {errorInfo && errorInfo.componentStack && (
          <div style={{ padding: '10px', backgroundColor: 'white', borderRadius: '4px', fontSize: '10px', color: '#666' }}>
            <strong>Component Stack:</strong><br />
            {errorInfo.componentStack}
          </div>
        )}

        <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '4px' }}>
          <strong>What to do:</strong><br />
          1. Take a screenshot of this error<br />
          2. Refresh the page (Ctrl+F5 or Cmd+Shift+R)<br />
          3. If the problem persists, contact support with the screenshot
        </div>
      </div>;
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;